/**
 * MFA rollout policy helpers.
 *
 * We're rolling MFA out softly: admins get a 30-day grace period from the
 * first time we see them in /admin/*. After that, no factor = hard block.
 *
 * The grace timestamp is recorded in localStorage. It is a UX hint only —
 * the authoritative check is `getAuthenticatorAssuranceLevel()` server-side.
 * A motivated user could clear localStorage to reset their own grace clock,
 * but that just delays their own enforcement; it cannot bypass the AAL2 step
 * once they enrol.
 */
export const MFA_GRACE_DAYS = 30;
const KEY_PREFIX = "admin.mfa.grace_start.";

export function getOrInitGraceStart(userId: string): number {
  const key = KEY_PREFIX + userId;
  try {
    const existing = localStorage.getItem(key);
    if (existing) {
      const n = Number(existing);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const now = Date.now();
    localStorage.setItem(key, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

export function clearGrace(userId: string): void {
  try {
    localStorage.removeItem(KEY_PREFIX + userId);
  } catch {
    /* ignore */
  }
}

export function graceDaysRemaining(graceStartMs: number): number {
  const elapsedMs = Date.now() - graceStartMs;
  const remainingMs = MFA_GRACE_DAYS * 24 * 60 * 60 * 1000 - elapsedMs;
  return Math.max(0, Math.ceil(remainingMs / (24 * 60 * 60 * 1000)));
}

export function isGraceExpired(graceStartMs: number): boolean {
  return graceDaysRemaining(graceStartMs) <= 0;
}
